
import React from "react";

/**
 * DailyInvoicePreview
 * Renders a preview of the daily invoice as HTML (for modal or print preview).
 * Props:
 *   logs: Array of log objects to display (should be filtered to selected logs)
 *   profile: user profile object (address, bank, etc)
 *   practice: selected practice object
 *   rateType, rateValue, invoiceNumber, total: invoice metadata
 */
const DailyInvoicePreview = ({
  logs = [],
  profile = {},
  practice = {},
  rateType = '',
  rateValue = '',
  invoiceNumber = '',
  total = null,
}) => {
  if (!logs || logs.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>
        No records selected.
      </div>
    );
  }

  // Format date helper
  const formatDate = (dateValue) => {
    if (!dateValue) return '';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return dateValue;
      return date.toLocaleDateString();
    } catch {
      return String(dateValue);
    }
  };

  // Group and format rows similar to PDF logic
  let tableRows = [];
  let totalAmount = 0;
  if (rateType === 'percentage') {
    // Calculate totals and collections
    let totalCollections = 0;
    const percentageRate = parseFloat(rateValue) || 0;
    
    // Group by date, showing collections (no percentage)
    const dateMap = new Map();
    logs.forEach(entry => {
      const dateKey = formatDate(entry.date || entry.formattedDate || '');
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, { patientDetails: [], collections: 0 });
      if (Array.isArray(entry.patients)) {
        entry.patients.forEach(patient => {
          const count = parseInt(patient.count) || 0;
          const rate = parseFloat(patient.rate) || 0;
          const patientCollections = count * rate;
          const pluralText = count !== 1 ? 's' : '';
          const desc = patient.description ? ` (${patient.description})` : '';
          
          dateMap.get(dateKey).patientDetails.push(`${count} patient${pluralText} @ £${rate.toFixed(2)}${desc}`);
          dateMap.get(dateKey).collections += patientCollections;
        });
      }
    });
    
    // Add daily rows showing collections (no percentage)
    dateMap.forEach((obj, dateKey) => {
      tableRows.push({
        date: dateKey,
        description: obj.patientDetails.join(' + '),
        amount: obj.collections,
      });
      totalCollections += obj.collections;
    });
    
    // Calculate percentage amount
    const percentageAmount = totalCollections * (percentageRate / 100);
    
    // Add summary rows
    tableRows.push({
      date: '',
      description: 'Total Charged to Patients',
      amount: totalCollections,
      isSubtotal: true
    });
    
    tableRows.push({
      date: '',
      description: `Commission (${percentageRate}%)`,
      amount: percentageAmount,
      isPercentage: true
    });
    
    tableRows.push({
      date: '',
      description: 'Invoice Total',
      amount: percentageAmount,
      isTotal: true
    });
    
    totalAmount = percentageAmount;
    
  } else if (rateType === 'per_patient') {
    // Group by date, combine patient types (similar to percentage but no percentage calculation)
    const dateMap = new Map();
    logs.forEach(entry => {
      const dateKey = formatDate(entry.date || entry.formattedDate || '');
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, { detailsArr: [], total: 0 });
      if (Array.isArray(entry.patients)) {
        entry.patients.forEach(patient => {
          const count = parseInt(patient.count) || 0;
          const rate = parseFloat(patient.rate) || 0;
          const amount = count * rate; // No percentage calculation for per_patient
          const patientDesc = patient.description ? ` (${patient.description})` : '';
          dateMap.get(dateKey).detailsArr.push(`${count} × £${rate.toFixed(2)}${patientDesc}`);
          dateMap.get(dateKey).total += amount;
        });
      }
    });
    dateMap.forEach((obj, dateKey) => {
      tableRows.push({
        date: dateKey,
        description: obj.detailsArr.join(', '),
        amount: obj.total,
      });
      totalAmount += obj.total;
    });
  } else if (rateType === 'hourly') {
    logs.forEach(entry => {
      const hours = parseFloat(entry.hours) || 0;
      const hourlyRate = parseFloat(rateValue) || 0;
      const amount = hours * hourlyRate;
      let description = entry.description || '';
      if (description.includes('=')) description = description.split('=')[0].trim();
      tableRows.push({
        date: formatDate(entry.date || entry.formattedDate || ''),
        description: `${hours} hours × £${hourlyRate.toFixed(2)}${description ? ` (${description})` : ''}`,
        amount,
      });
      totalAmount += amount;
    });
  } else if (rateType === 'fixed') {
    logs.forEach(entry => {
      const fixedAmount = parseFloat(rateValue) || 0;
      let description = entry.description || '';
      if (description.includes('=')) description = description.split('=')[0].trim();
      tableRows.push({
        date: formatDate(entry.date || entry.formattedDate || ''),
        description: `Fixed rate${description ? ` (${description})` : ''}`,
        amount: fixedAmount,
      });
      totalAmount += fixedAmount;
    });
  } else {
    logs.forEach(entry => {
      let description = entry.description || '';
      if (description.includes('=')) description = description.split('=')[0].trim();
      const amount = typeof entry.amount === 'number' ? entry.amount : parseFloat(entry.amount) || 0;
      tableRows.push({
        date: formatDate(entry.date || entry.formattedDate || ''),
        description,
        amount,
      });
      totalAmount += amount;
    });
  }

  // Use provided total if available (for consistency)
  if (typeof total === 'number') totalAmount = total;
  if (total && typeof total.amount === 'number') totalAmount = total.amount;

  // Helper for address block
  const renderAddress = (obj, isPractice = false) => {
    if (!obj) return null;
    // For practice, match PDF/MonthlyInvoice logic: filter out 'NA', support prac_city/prac_postcode
    if (isPractice) {
      // Use all possible practice address fields, filter NA/null/empty, and render name bold
      const lines = [
        obj.prac_name ? <strong key="prac_name">{obj.prac_name}</strong> : null,
        obj.address1 && obj.address1 !== 'NA' ? obj.address1 : null,
        obj.address2 && obj.address2 !== 'NA' ? obj.address2 : null,
        obj.address3 && obj.address3 !== 'NA' ? obj.address3 : null,
        obj.address4 && obj.address4 !== 'NA' ? obj.address4 : null,
        obj.address5 && obj.address5 !== 'NA' ? obj.address5 : null,
        (obj.prac_city && obj.prac_city !== 'NA') ? obj.prac_city : (obj.city && obj.city !== 'NA' ? obj.city : null),
        (obj.prac_county && obj.prac_county !== 'NA') ? obj.prac_county : (obj.county && obj.county !== 'NA' ? obj.county : null),
        (obj.prac_country && obj.prac_country !== 'NA') ? obj.prac_country : (obj.country && obj.country !== 'NA' ? obj.country : null),
        (obj.prac_postcode && obj.prac_postcode !== 'NA') ? obj.prac_postcode : (obj.postcode && obj.postcode !== 'NA' ? obj.postcode : null)
      ].filter(Boolean);
      if (lines.length === 0) return null;
      return (
        <div style={{ marginBottom: 4, textAlign: 'right' }}>
          {lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          {obj.email && <div>Email: {obj.email}</div>}
          {obj.phone && <div>Phone: {obj.phone}</div>}
        </div>
      );
    } else {
      // User address block (keep as before, but filter 'NA')
      const lines = [
        obj.name,
        obj.businessName,
        obj.address1 && obj.address1 !== 'NA' ? obj.address1 : null,
        obj.address2 && obj.address2 !== 'NA' ? obj.address2 : null,
        obj.address3 && obj.address3 !== 'NA' ? obj.address3 : null,
        obj.address4 && obj.address4 !== 'NA' ? obj.address4 : null,
        obj.city && obj.city !== 'NA' ? obj.city : null,
        obj.county && obj.county !== 'NA' ? obj.county : null,
        obj.country && obj.country !== 'NA' ? obj.country : null,
        obj.postcode && obj.postcode !== 'NA' ? obj.postcode : null
      ].filter(Boolean);
      if (lines.length === 0) return null;
      return (
        <div style={{ marginBottom: 4 }}>
          {lines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          {obj.email && <div>Email: {obj.email}</div>}
          {obj.phone && <div>Phone: {obj.phone}</div>}
        </div>
      );
    }
  };

  // Today's date
  const today = new Date();
  const invoiceDate = today.toLocaleDateString();

  // Unified light style for both light and dark mode
  return (
    <div style={{
      maxWidth: 700,
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif',
      fontSize: 15,
      color: '#222',
      background: '#fff',
      borderRadius: 8,
      boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)',
      padding: 24
    }}>
      {/* INVOICE title at the very top, centered */}
      <div style={{ width: '100%', textAlign: 'center', marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 22, color: '#4b0082', letterSpacing: 1 }}>INVOICE</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <div>{renderAddress(profile)}</div>
        <div style={{ textAlign: 'right' }}>{renderAddress(practice, true)}</div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <div style={{ textAlign: 'right', fontSize: 15 }}>
          <div>Invoice #: <b>{invoiceNumber || 'PREVIEW'}</b></div>
          <div>Date: {invoiceDate}</div>
        </div>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 15 }}>
        <thead>
          <tr style={{ background: '#f3f3f3' }}>
            <th style={{ padding: '8px', textAlign: 'left', color: '#222' }}>Date</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#222' }}>Description</th>
            <th style={{ padding: '8px', textAlign: 'right', color: '#222' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {tableRows.map((row, idx) => {
            const isSpecialRow = row.isSubtotal || row.isPercentage || row.isTotal;
            const rowStyle = isSpecialRow 
              ? { borderTop: '2px solid #4b0082', backgroundColor: '#f9f9f9', fontWeight: 'bold' }
              : { borderBottom: '1px solid #eee' };
            
            return (
              <tr key={idx} style={rowStyle}>
                <td style={{ padding: '8px', fontWeight: isSpecialRow ? 'bold' : 'normal' }}>{row.date}</td>
                <td style={{ padding: '8px', fontWeight: isSpecialRow ? 'bold' : 'normal' }}>{row.description}</td>
                <td style={{ padding: '8px', textAlign: 'right', fontWeight: isSpecialRow ? 'bold' : 'normal' }}>£{Number(row.amount).toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2} style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 'bold' }}>Total</td>
            <td style={{ padding: '10px 8px', textAlign: 'right', fontWeight: 'bold' }}>£{Number(totalAmount).toFixed(2)}</td>
          </tr>
        </tfoot>
      </table>
      <div style={{ marginTop: 24, fontSize: 14 }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Bank Details:</div>
        <div>Bank: {profile.bankName || ''}</div>
        <div>Sort Code: {profile.sortCode || ''}</div>
        <div>Account Number: {profile.accountNumber || ''}</div>
        <div style={{ marginTop: 10 }}>Payment is due within 30 days from date of invoice.</div>
        {profile.notes && (
          <div style={{ marginTop: 10, color: '#555' }}>
            <div style={{ fontWeight: 600 }}>Notes:</div>
            {profile.notes.split('\n').map((line, i) => <div key={i}>{line}</div>)}
          </div>
        )}
      </div>
    </div>
  );
};

export default DailyInvoicePreview;
